import * as React from 'react';

import {colors} from '../../colors/colors';
import type {ColorName} from '../../colors/colors';

import {DEFAULT_ICON_SIZE, DEFAULT_SUB_ICON_SIZE} from './consts';
import type {TimelineEntryIcon as TimelineEntryIconType} from './types';

import styles from './timeline-entry-icon.module.css';

const DEFAULT_ICON_FILL: ColorName = 'white';
const SMALL_ICON_SIZE_OFFSET = 2;
const LAST_ENTRY_SMALL_LINE_HEIGHT_OFFSET = 11;
const LAST_ENTRY_LINE_HEIGHT_OFFSET = 15;
const LAST_ENTRY_SMALL_LEFT_PADDING_OFFSET = 14;
const LAST_ENTRY_LEFT_PADDING_OFFSET = 13;

type Props = {
    icon: TimelineEntryIconType;
    gutterContainerRef?: any;
    isSmall?: boolean;
    isLastEntry?: boolean;
    onClickOpen?: (e: React.SyntheticEvent<any>) => void;
    hasGutterLine?: boolean;
};

export function TimelineEntryIcon(props: Props) {
    const {gutterContainerRef, isSmall, isLastEntry, hasGutterLine = true} = props;

    const [gutterLineHeight, setGutterLineHeight] = React.useState<number>(0);

    // Sets the gutter height on mount
    React.useEffect(() => {
        if (hasGutterLine && gutterContainerRef && gutterContainerRef.current) {
            setGutterLineHeight(gutterContainerRef.current.clientHeight);
        }
    }, [hasGutterLine, gutterContainerRef]);

    // Updates the gutter height if component re-renders & container ref height changes
    if (
        hasGutterLine &&
        gutterContainerRef &&
        gutterContainerRef.current &&
        gutterContainerRef.current.clientHeight !== gutterLineHeight
    ) {
        setGutterLineHeight(gutterContainerRef.current.clientHeight);
    }

    const {subIcon: SubIcon, backgroundColor, fill, size, icon: Icon} = props.icon;

    const iconFill = fill ? colors[fill] : colors[DEFAULT_ICON_FILL];
    const iconSize = size ? size : DEFAULT_ICON_SIZE;
    const iconSizeSmall = iconSize - SMALL_ICON_SIZE_OFFSET;
    const subIconSizeSmall = DEFAULT_SUB_ICON_SIZE - SMALL_ICON_SIZE_OFFSET;

    const lastEntryGutterLineHeight = isSmall
        ? gutterLineHeight - LAST_ENTRY_SMALL_LINE_HEIGHT_OFFSET
        : gutterLineHeight - LAST_ENTRY_LINE_HEIGHT_OFFSET;

    return (
        <div
            onClick={props.onClickOpen}
            className={isSmall ? styles['timeline-icon--small'] : styles['timeline-icon']}
        >
            <div
                style={{color: iconFill, backgroundColor: colors[backgroundColor]}}
                className={
                    props.onClickOpen
                        ? styles['icon-container--clickable']
                        : styles['icon-container']
                }
            >
                <Icon size={isSmall ? iconSizeSmall : iconSize} color={iconFill} fill={iconFill} />
            </div>
            {SubIcon ? (
                <div
                    style={{backgroundColor: colors[backgroundColor]}}
                    className={
                        isSmall ? styles['sub-icon-container--small'] : styles['sub-icon-container']
                    }
                >
                    <SubIcon
                        size={isSmall ? subIconSizeSmall : DEFAULT_SUB_ICON_SIZE}
                        color={iconFill}
                        fill={iconFill}
                    />
                </div>
            ) : undefined}
            {gutterLineHeight ? (
                <div
                    style={{
                        height: isLastEntry ? lastEntryGutterLineHeight : gutterLineHeight,
                    }}
                    className={isSmall ? styles['gutter-line--small'] : styles['gutter-line']}
                />
            ) : undefined}
            {gutterLineHeight && isLastEntry ? (
                <div
                    style={{
                        position: 'absolute',
                        left: isSmall
                            ? LAST_ENTRY_SMALL_LEFT_PADDING_OFFSET
                            : LAST_ENTRY_LEFT_PADDING_OFFSET,
                    }}
                    className={isSmall ? styles['end-circle--small'] : styles['end-circle']}
                />
            ) : undefined}
        </div>
    );
}
